import { Edit2 } from "lucide-react";
import '@app/App.css';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Button } from "@comps/ui/button";
import { put } from "@utils/utils";
import { useState } from "react";
import { defaultObjectIcon, ObjectDto, ObjectInfos } from "@dto/objects";
import { useModelStore } from "src/store";
import { useTranslation } from "react-i18next";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_ObjectMenuItem, useHover } from "@utils/twClasses";
import { translate } from "src/i18n/i18n";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { LucideIconType, SaxIconType } from "@dto/CompozzIcon";
import { IconPicker } from "@comps/ui/IconPicker";
import { BASE_URL } from "@utils/constants";

interface ObjectMenuEditItemProps {
  objectInfo: ObjectInfos;
  onObjectUpdate: (object: ObjectDto) => void;
  closeMenu: () => void;
}

function ObjectMenuEditItem({ objectInfo, onObjectUpdate, closeMenu }: ObjectMenuEditItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

  const objectUpdated = (object: ObjectDto) => {
    onObjectUpdate(object);
    setIsDialogOpen(false);
    closeMenu();
  }

  function onCancel() {
    setIsDialogOpen(false);
    closeMenu();
  }
    
  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(true);
  }

  function handleDialogBlur(event: React.FocusEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_ObjectMenuItem + ` ${objectInfo === null ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Edit2 className="ObjectMenuItemIcon" />{t("object.button.edit.text")}</DialogTrigger>
      <DialogContent onClick={(e) => e.stopPropagation()} onBlur={(e) => handleDialogBlur(e)}>
        <DialogHeader>
          <DialogTitle>{t("object.dialog.edit.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("object.dialog.edit.description")}</DialogDescription>
        </DialogHeader>
        {objectInfo && <ObjectEditForm initialObject={objectInfo.object} onObjectUpdate={objectUpdated} onCancel={onCancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface ObjectEditFormProps {
  initialObject: ObjectDto;
  onObjectUpdate: (object: ObjectDto) => void;
  onCancel: () => void;
}

function ObjectEditForm({ initialObject, onObjectUpdate, onCancel }: ObjectEditFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    objectName: z.string().min(1, {
      message: t("object.dialog.error.name.missing"),
    }),
    objectIcon: z.object({
      name: z.string(),
      type: z.enum([SaxIconType, LucideIconType]),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      objectName: initialObject.name,
      objectIcon: initialObject.icon ?? defaultObjectIcon,
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {

    // No need to update the workspace if the name is the same
    if (values.objectName === initialObject.name && values.objectIcon === initialObject.icon) {
      return;
    }

    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }

    const updatedObject = {
      ...initialObject,
      name: values.objectName,
      icon: values.objectIcon,
    }
    put<ObjectDto>(`${BASE_URL}/model/workspaces/${workspace._key}/objects`, updatedObject).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        }
      } else {
        setError(null);
        onObjectUpdate(updatedObject);
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="objectName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("object.dialog.edit.name.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("object.dialog.edit.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
            control={form.control}
            name="objectIcon"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>{t("object.dialog.edit.icon.label")}</FormLabel>
                  <FormControl>
                    <IconPicker icon={field.value} color="black" onChange={(icon) => field.onChange(icon)} side="right" align="center" sideOffset={10} />
                  </FormControl>
                  <FormMessage />
              </FormItem>
            )}}
          />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default ObjectMenuEditItem;
